'use client'

import { EntityType, getRecentSearches, SearchDocument, SearchParams, SearchResults } from '@/shared/api/global-search'
import { useGlobalSearch } from '@/shared/hooks/search/useGlobalSearch'
import { useKeywordHistories } from '@/shared/hooks/search/useKeywordHistories'
import { useLoadMoreSearch } from '@/shared/hooks/search/useLoadMoreSearch'
import { useSaveSearchHistory } from '@/shared/hooks/search/useSaveSearchHistory'
import { useSearchAutocomplete } from '@/shared/hooks/search/useSearchAutocomplete'
import { useSearchHistories } from '@/shared/hooks/search/useSearchHistories'
import { useDebounce } from '@/shared/hooks/useDebounce'
import { Button } from '@workspace/ui/components/Button'
import { Dialog } from '@workspace/ui/components/Dialog'
import { ScrollArea } from '@workspace/ui/components/ScrollArea'
import { BsSearchField } from '@workspace/ui/components/Searchfield'
import { X } from 'lucide-react'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, ModalOverlay } from 'react-aria-components'
import { useTranslation } from 'react-i18next'
import { FolderItem, ProjectItem, RecentSearches } from '../global-search'
import { Filter } from '../global-search/Filter'
import { KeywordSuggestions } from '../global-search/KeywordSuggestions'
import { LoadMoreButton } from '../global-search/LoadMoreButton'
import { SearchCategoryTabs } from '../global-search/SearchCategoryTabs'
import { TaskItem } from '../global-search/TaskItem'
import { DocumentContentItem, DocumentItem } from '../global-search/DocumentItem'

export interface SearchResultItem {
    id: string
    type: 'document' | 'folder' | 'project'
    title: string
    description?: string
    code?: string
    icon?: React.ComponentType<{ className?: string }>
}

interface GlobalSearchDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

const hasActiveFilters = (filters: Record<string, string[]>) => {
    return Object.values(filters).some(values => values.length > 0)
}

const CATEGORIES_CONFIG = [
    { key: 'projects', label: 'Dự án', type: 'PROJECT' },
    { key: 'tasks', label: 'Công việc', type: 'TASK' },
    { key: 'folders', label: 'Thư mục', type: 'FOLDER' },
    { key: 'documents', label: 'Tài liệu', type: 'DOCUMENT' },
    { key: 'content', label: 'Nội dung tài liệu', type: 'DOCUMENT_CONTENT' },
] as const

const groupResultsByCategory = (data: SearchResults) => {
    const results = data.results || []
    const initialResults: any = {}

    // Initialize groups based on config
    CATEGORIES_CONFIG.forEach(cat => {
        initialResults[cat.key] = {
            data: [],
            count: data.typeCounts?.[cat.type as keyof typeof data.typeCounts] ?? 0,
        }
    })

    // Group results in one pass
    results.forEach(item => {
        const categoryConfig = CATEGORIES_CONFIG.find(c => c.type === item.entityType)
        if (categoryConfig) {
            initialResults[categoryConfig.key].data.push(item)
        }
    })

    return initialResults
}

export function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps) {
    const { t } = useTranslation()
    const [searchQuery, setSearchQuery] = useState('')
    const [filters, setFilters] = useState<Record<string, string[]>>({})
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
    const trimmedQuery = useMemo(() => searchQuery.trim(), [searchQuery])

    // Load recent searches from localStorage
    const [results, setResults] = useState<{
        projects: {
            data: SearchDocument[]
            count: number
        }
        tasks: {
            data: SearchDocument[]
            count: number
        }
        folders: {
            data: SearchDocument[]
            count: number
        }
        documents: {
            data: SearchDocument[]
            count: number
        }
        content: {
            data: SearchDocument[]
            count: number
        }
    }>({
        projects: { data: [], count: 0 },
        tasks: { data: [], count: 0 },
        folders: { data: [], count: 0 },
        documents: { data: [], count: 0 },
        content: { data: [], count: 0 },
    })

    const hasFilter = useMemo(() => hasActiveFilters(filters), [filters])
    // Debounce search query
    const debouncedQuery = useDebounce(trimmedQuery, 500)

    const searchParams = useMemo(
        (): SearchParams => ({
            q: debouncedQuery || (hasActiveFilters(filters) ? '*' : ''),
            entityType: selectedCategory
                ? (CATEGORIES_CONFIG.find(c => c.key === selectedCategory)?.type as EntityType)
                : null,
            regionIds: filters.region || [],
            areaIds: filters.area || [],
            projectIds: filters.project || [],
            documentTypeIds: filters.documentType || [],
            assignedUserIds: filters.responsibleUser || [],
            statuses: filters.status || [],
            top: 10,
            skip: 0,
        }),
        [debouncedQuery, filters, selectedCategory],
    )

    const { data: searchResultsData, isLoading: isInitialSearching } = useGlobalSearch(searchParams, open)
    const { data: autocompleteData } = useSearchAutocomplete(debouncedQuery, open)
    const { data: keywordHistoriesData } = useKeywordHistories(open)
    const { data: searchHistoriesData } = useSearchHistories(open)

    const saveSearchHistoryMutation = useSaveSearchHistory()

    const onClose = useCallback(() => {
        onOpenChange(false)
        setSearchQuery('')
        setFilters({})
        setSelectedCategory(null)
    }, [onOpenChange])

    const handleItemClick = useCallback(
        (item: any) => {
            saveSearchHistoryMutation.mutate({
                ...item,
            })
            onClose()
        },
        [saveSearchHistoryMutation, onClose],
    )

    // Update local results state when hook returns new data
    useEffect(() => {
        if (searchResultsData) {
            setResults(groupResultsByCategory(searchResultsData))
        } else if (!debouncedQuery) {
            setResults({
                projects: { data: [], count: 0 },
                tasks: { data: [], count: 0 },
                folders: { data: [], count: 0 },
                documents: { data: [], count: 0 },
                content: { data: [], count: 0 },
            })
        }
    }, [searchResultsData, debouncedQuery])

    const categoriesCount = useMemo(
        () =>
            CATEGORIES_CONFIG.map(config => ({
                id: config.key,
                label: config.label,
                count: results[config.key].count,
            })),
        [results],
    )

    const totalResultsCount = useMemo(() => {
        if (selectedCategory) {
            return results[selectedCategory as keyof typeof results]?.count || 0
        }
        return Object.values(results).reduce((sum, cat) => sum + cat.count, 0)
    }, [results, selectedCategory])

    return (
        <ModalOverlay
            isOpen={open}
            onOpenChange={onOpenChange}
            isDismissable={true}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
        >
            <Modal className="w-full max-w-[1240px] mx-4 bg-background rounded-lg shadow-lg flex flex-col ">
                <Dialog
                    role="dialog"
                    className="outline-none flex flex-col h-[calc(100vh-100px)] max-h-[720px] space-y-4 p-6 relative"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold">Tìm kiếm</h2>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-foreground"
                            onPress={() => onOpenChange(false)}
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Search Header */}

                    <BsSearchField
                        autoTrim
                        value={searchQuery}
                        onChange={setSearchQuery}
                        autoFocus
                        className="w-full h-9 text-base rounded-lg"
                        placeholder="Tìm kiếm dự án, tài liệu"
                        clearNode={
                            <Button variant="ghost" size="icon" className="text-muted-500">
                                Xoá
                            </Button>
                        }
                    />

                    {/* Filter Section */}
                    <Filter onFilterChange={setFilters} />

                    {/* Tìm kiếm gần đây */}
                    <KeywordSuggestions
                        mode={'recent'}
                        suggestions={keywordHistoriesData?.map(item => ({ text: item.q, queryPlusText: item.q }))}
                        onSelect={setSearchQuery}
                        isHidden={!!trimmedQuery || hasFilter}
                    />

                    <SearchCategoryTabs
                        categories={categoriesCount}
                        selectedCategory={selectedCategory}
                        onSelect={setSelectedCategory}
                        isHidden={!totalResultsCount && !selectedCategory}
                    />
                    {/* Keywork gợi ý */}
                    <KeywordSuggestions
                        mode={'suggestion'}
                        suggestions={autocompleteData?.results}
                        onSelect={setSearchQuery}
                        isHidden={!!totalResultsCount || isInitialSearching || !!selectedCategory}
                    />

                    {/* Content Area - Scrollable */}
                    <ScrollArea className="h-full flex-1 overflow-y-auto pb-5 pr-1 relative">
                        <RecentSearches
                            searches={searchHistoriesData}
                            isHidden={isInitialSearching || !!trimmedQuery || hasFilter}
                            onClose={onClose}
                        />

                        {!isInitialSearching &&
                            CATEGORIES_CONFIG.filter(
                                category => !selectedCategory || category.key === selectedCategory,
                            ).map(category => (
                                <CategoryBox
                                    key={category.key}
                                    data={results[category.key].data}
                                    countData={results[category.key].count}
                                    type={category.type}
                                    category={category.key}
                                    label={category.label}
                                    selectedCategory={selectedCategory}
                                    searchQuery={debouncedQuery}
                                    filters={filters}
                                    setResults={setResults}
                                    onClose={onClose}
                                    onItemClick={handleItemClick}
                                />
                            ))}

                        {isInitialSearching && (
                            <p className="text-sm text-muted-foreground absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pb-2">
                                Đang tìm kiếm...
                            </p>
                        )}

                        {!totalResultsCount && trimmedQuery && !isInitialSearching && (
                            <p className="text-sm text-muted-foreground absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pb-2">
                                Không tìm thấy kết quả
                            </p>
                        )}
                    </ScrollArea>

                    {totalResultsCount > 0 && (
                        <div className="text-xs text-foreground absolute bottom-0 left-1/2 -translate-x-1/2 pb-2">
                            {totalResultsCount} kết quả
                        </div>
                    )}
                </Dialog>
            </Modal>
        </ModalOverlay>
    )
}

// Search Result Card Component

const CategoryBox = memo(
    ({
        data,
        type,
        category,
        label,
        selectedCategory,
        searchQuery,
        filters,
        setResults,
        onClose,
        onItemClick,
        countData,
    }: {
        data: SearchDocument[]
        type: EntityType
        category: string
        label: string
        selectedCategory?: string | null
        searchQuery: string
        filters: Record<string, string[]>
        setResults: React.Dispatch<React.SetStateAction<any>>
        onClose: () => void
        onItemClick: (item: SearchDocument) => void
        countData: number
    }) => {
        const loadMoreMutation = useLoadMoreSearch()
        if (!data?.length) return null

        const handleLoadMore = async () => {
            try {
                const searchParams: SearchParams = {
                    q: searchQuery,
                    entityType: type,
                    regionIds: filters.region || [],
                    areaIds: filters.area || [],
                    projectIds: filters.project || [],
                    documentTypeIds: filters.documentType || [],
                    assignedUserIds: filters.assignedUser || [],
                    statuses: filters.status || [],
                    top: 10,
                    skip: data.length,
                }

                const moreResultsResponse = await loadMoreMutation.mutateAsync(searchParams)
                const moreResults = moreResultsResponse.results || []

                setResults((prev: any) => ({
                    ...prev,
                    [category]: {
                        ...prev[category],
                        data: [...prev[category].data, ...moreResults],
                    },
                }))
            } catch (error) {
                console.error('Error loading more:', error)
            }
        }

        const handleItem = (item: SearchDocument) => {
            const highlights = item['@search.highlights']
            switch (type) {
                case 'PROJECT':
                    return (
                        <ProjectItem
                            key={item.id}
                            title={item.title}
                            code={item?.code}
                            description={item?.description}
                            highlights={highlights}
                            onClick={() => onItemClick(item)}
                            id={item.id}
                        />
                    )
                case 'FOLDER':
                    return (
                        <FolderItem
                            key={item.id}
                            title={item.title}
                            path={item?.path}
                            highlights={highlights}
                            onClick={() => onItemClick(item)}
                            id={item.id}
                            projectId={item.projectId}
                        />
                    )
                case 'DOCUMENT':
                    return (
                        <DocumentItem
                            key={item.id}
                            title={item.title}
                            code={item?.code}
                            description={item?.description}
                            highlights={highlights}
                            onClick={() => onItemClick(item)}
                            projectId={item.projectId}
                            id={item.id}
                        />
                    )
                case 'DOCUMENT_CONTENT':
                    return (
                        <DocumentContentItem
                            key={item.id}
                            title={item.title}
                            code={item?.code}
                            content={item?.content}
                            highlights={highlights}
                            onClick={() => onItemClick(item)}
                            projectId={item.projectId}
                            id={item.id}
                            documentId={item.documentId}
                        />
                    )

                case 'TASK':
                    return (
                        <TaskItem
                            key={item.id}
                            title={item.title}
                            code={item?.code}
                            description={item?.description}
                            path={item?.path}
                            highlights={highlights}
                            onClick={() => onItemClick(item)}
                            id={item.id}
                            projectId={item.projectId}
                        />
                    )
                default:
                    return (
                        <DocumentItem
                            key={item.id}
                            title={item.title}
                            code={item?.code}
                            description={item?.description}
                            highlights={highlights}
                            onClick={() => onItemClick(item)}
                            projectId={item.projectId}
                            id={item.id}
                        />
                    )
            }
        }

        const hasLoadMore = data.length < countData

        return (
            <div>
                <h6 className="text-sm font-semibold capitalize mb-2 mt-4">{label}</h6>
                <div>
                    {data?.map(item => {
                        return handleItem(item)
                    })}
                </div>
                {(selectedCategory === category || !selectedCategory) && hasLoadMore && (
                    <LoadMoreButton onPress={handleLoadMore} title={label} loading={loadMoreMutation.isPending} />
                )}
            </div>
        )
    },
)

CategoryBox.displayName = 'CategoryBox'
